/**
 * Plans Page - SaaS Plans Management
 * 
 * Manage subscription plans:
 * - Create/Edit/Delete plans
 * - Configure pricing, limits, modules
 */

import React, { useState, useEffect } from 'react';
import {
  Package, Plus, Edit, Trash2, Check, X, Save,
  MoreVertical, RefreshCw, Building2, Users, Boxes
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { api } from '../../App';
import { toast } from 'sonner';

const PERIODICITIES = [
  { value: 'MONTHLY', label: 'Mensuel' },
  { value: 'QUARTERLY', label: 'Trimestriel' },
  { value: 'YEARLY', label: 'Annuel' },
  { value: 'LIFETIME', label: 'À vie' }
];

const MODULES = [
  { key: 'reservations', label: 'Réservations', icon: '📅' },
  { key: 'pos', label: 'Point de vente', icon: '🛒' },
  { key: 'housekeeping', label: 'Housekeeping', icon: '🧹' },
  { key: 'hr', label: 'Ressources Humaines', icon: '👥' },
  { key: 'finance', label: 'Finance', icon: '💰' },
  { key: 'reports', label: 'Rapports', icon: '📊' },
  { key: 'api_access', label: 'Accès API', icon: '🔌' },
  { key: 'ical_sync', label: 'Sync iCal', icon: '🔄' },
  { key: 'multi_property', label: 'Multi-propriétés', icon: '🏢' }
];

const DEFAULT_PLAN = {
  name: '',
  code: '',
  description: '',
  price: 0,
  currency: 'MAD',
  periodicity: 'MONTHLY',
  status: 'ACTIVE',
  is_public: true,
  is_default: false,
  max_tenants: 1,
  max_users: 5,
  trial_days: 14,
  color: '#3B82F6',
  allowed_modules: {},
  features: {},
  limits: {}
};

const PlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_PLAN);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/plans');
      setPlans(data.plans || data);
    } catch (error) {
      toast.error('Erreur lors du chargement des plans');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingPlan(null);
    setFormData(DEFAULT_PLAN);
    setDialogOpen(true);
  };

  const handleOpenEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      ...DEFAULT_PLAN,
      ...plan,
      allowed_modules: plan.allowed_modules || {},
      features: plan.features || {},
      limits: plan.limits || {}
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.code) {
      toast.error('Nom et code requis');
      return;
    }

    try {
      if (editingPlan) {
        await api.put(`/admin/plans/${editingPlan.id}`, formData);
        toast.success('Plan mis à jour');
      } else {
        await api.post('/admin/plans', formData);
        toast.success('Plan créé');
      }
      setDialogOpen(false);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (plan) => {
    if (!confirm(`Supprimer le plan "${plan.name}" ?`)) return;
    
    try {
      await api.delete(`/admin/plans/${plan.id}`);
      toast.success('Plan supprimé');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleToggleStatus = async (plan) => {
    try {
      await api.put(`/admin/plans/${plan.id}`, {
        status: plan.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
      });
      toast.success('Statut mis à jour');
      fetchPlans();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleModule = (moduleKey) => {
    setFormData(prev => ({
      ...prev,
      allowed_modules: {
        ...prev.allowed_modules,
        [moduleKey]: !prev.allowed_modules[moduleKey]
      }
    }));
  };

  return (
    <div className="p-6 space-y-6" data-testid="plans-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Plans Tarifaires</h1>
          <p className="text-muted-foreground">Configuration des offres SaaS</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchPlans} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={handleOpenCreate} data-testid="create-plan-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Plan
          </Button>
        </div>
      </div>

      {/* Plans Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      ) : plans.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun plan configuré</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map(plan => (
            <Card 
              key={plan.id} 
              className={`relative ${plan.status !== 'ACTIVE' ? 'opacity-60' : ''}`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.is_default && (
                <Badge className="absolute -top-2 -right-2 bg-violet-600">Par défaut</Badge>
              )}
              
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: plan.color || '#3B82F6' }}
                    />
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleOpenEdit(plan)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleStatus(plan)}>
                        {plan.status === 'ACTIVE' ? (
                          <><X className="w-4 h-4 mr-2" /> Désactiver</>
                        ) : (
                          <><Check className="w-4 h-4 mr-2" /> Activer</>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => handleDelete(plan)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <CardDescription className="text-xs">
                  Code: {plan.code}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Price */}
                <div className="text-center py-4 bg-muted/50 rounded-lg">
                  <p className="text-3xl font-bold">
                    {parseFloat(plan.price).toLocaleString('fr-FR')} {plan.currency}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {PERIODICITIES.find(p => p.value === plan.periodicity)?.label || plan.periodicity}
                  </p>
                </div>
                
                {/* Limits */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      Riads
                    </span>
                    <span className="font-medium">
                      {plan.max_tenants === 0 ? 'Illimité' : plan.max_tenants}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      Utilisateurs
                    </span>
                    <span className="font-medium">
                      {plan.max_users === 0 ? 'Illimité' : plan.max_users}
                    </span>
                  </div>
                </div>
                
                {/* Modules */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Modules inclus</p>
                  <div className="flex flex-wrap gap-1">
                    {MODULES.filter(m => plan.allowed_modules?.[m.key]).map(m => (
                      <Badge key={m.key} variant="secondary" className="text-xs">
                        {m.icon} {m.label}
                      </Badge>
                    ))}
                    {!Object.values(plan.allowed_modules || {}).some(v => v) && (
                      <span className="text-xs text-muted-foreground">Tous les modules</span>
                    )}
                  </div>
                </div>
                
                {/* Status */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <Badge className={plan.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                    {plan.status === 'ACTIVE' ? 'Actif' : 'Inactif'}
                  </Badge>
                  {plan.is_public && (
                    <Badge variant="outline">Public</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Modifier le plan' : 'Nouveau plan'}</DialogTitle>
            <DialogDescription>
              {editingPlan ? `Modification de ${editingPlan.name}` : 'Créer un nouveau plan tarifaire'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Pro, Enterprise..."
                />
              </div>
              <div>
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="PRO, ENTERPRISE..."
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Description du plan..."
                  rows={2}
                />
              </div>
            </div>
            
            {/* Pricing */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Prix</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <Label>Devise</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MAD">MAD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Périodicité</Label>
                <Select value={formData.periodicity} onValueChange={(v) => setFormData(prev => ({ ...prev, periodicity: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODICITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Limits */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Max Riads</Label>
                <Input
                  type="number"
                  value={formData.max_tenants}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_tenants: parseInt(e.target.value) || 0 }))}
                  min="0"
                  placeholder="0 = illimité"
                />
              </div>
              <div>
                <Label>Max Utilisateurs</Label>
                <Input
                  type="number"
                  value={formData.max_users}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_users: parseInt(e.target.value) || 0 }))}
                  min="0"
                  placeholder="0 = illimité"
                />
              </div>
              <div>
                <Label>Jours d'essai</Label>
                <Input
                  type="number"
                  value={formData.trial_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, trial_days: parseInt(e.target.value) || 0 }))}
                  min="0"
                />
              </div>
            </div>
            
            {/* Modules */}
            <div>
              <Label className="mb-3 block">Modules autorisés</Label>
              <div className="grid grid-cols-3 gap-2">
                {MODULES.map(module => (
                  <div
                    key={module.key}
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                      formData.allowed_modules[module.key] ? 'bg-violet-50 border-violet-300' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => toggleModule(module.key)}
                  >
                    <span>{module.icon}</span>
                    <span className="text-sm">{module.label}</span>
                    {formData.allowed_modules[module.key] && (
                      <Check className="w-4 h-4 text-violet-600 ml-auto" />
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Si aucun module sélectionné, tous les modules sont inclus par défaut.
              </p>
            </div>
            
            {/* Options */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Couleur</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Actif</SelectItem>
                    <SelectItem value="INACTIVE">Inactif</SelectItem>
                    <SelectItem value="DEPRECATED">Déprécié</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Switches */}
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_public}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_public: v }))}
                />
                <Label>Public</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_default: v }))}
                />
                <Label>Par défaut</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} data-testid="save-plan-btn">
              <Save className="w-4 h-4 mr-2" />
              {editingPlan ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlansPage;
